// settingsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const handleError = (err, rejectWithValue) =>
  rejectWithValue(err.response?.data || err.message);

// ==============================
// Generic POST thunk creator
// ==============================
const createPostThunk = (name, url) =>
  createAsyncThunk(name, async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseURL}${url}`, payload);
      return res.data;
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  });

// ==============================
// Thunks
// ==============================
export const fetchSettings = createPostThunk(
  "settings/fetchSettings",
  "/emis_pathology/api/settings/",
);

// Keywords
export const addKeyword = createPostThunk(
  "settings/addKeyword",
  "/emis_pathology/api/settings/keywords/",
);
export const deleteKeywords = createPostThunk(
  "settings/deleteKeywords",
  "/emis_pathology/api/settings/keywords/delete/",
);
export const activateKeywords = createPostThunk(
  "settings/activateKeywords",
  "/emis_pathology/api/settings/keywords/activate/",
);
export const deactivateKeywords = createPostThunk(
  "settings/deactivateKeywords",
  "/emis_pathology/api/settings/keywords/deactivate/",
);

// NHS
export const addNhs = createPostThunk(
  "settings/addNhs",
  "/emis_pathology/api/settings/nhs/",
);
export const deleteNhs = createPostThunk(
  "settings/deleteNhs",
  "/emis_pathology/api/settings/nhs/delete/",
);
export const activateNhs = createPostThunk(
  "settings/activateNhs",
  "/emis_pathology/api/settings/nhs/activate/",
);
export const deactivateNhs = createPostThunk(
  "settings/deactivateNhs",
  "/emis_pathology/api/settings/nhs/deactivate/",
);

// ==============================
// Slice
// ==============================
const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    keywords: [], // { keyword: string, enabled: boolean }
    nhsNumbers: [], // { nhs_number: string, reason: string, enabled: boolean }
    loading: false,
    error: null,
  },
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const thunks = [
      fetchSettings,
      addKeyword,
      deleteKeywords,
      activateKeywords,
      deactivateKeywords,
      addNhs,
      deleteNhs,
      activateNhs,
      deactivateNhs,
    ];

    thunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false;

          // Update keywords
          if (Array.isArray(action.payload?.keywords)) {
            state.keywords = action.payload.keywords;
          }

          // Update NHS numbers
          if (Array.isArray(action.payload?.nhs_numbers)) {
            state.nhsNumbers = action.payload.nhs_numbers;
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload || action.error.message;
        });
    });
  },
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;
